import { useEffect, useState } from 'react'

// import { Check } from 'assets/Icons'
import clsx from 'clsx'
import {
  // Badge,
  // Button,
  Card,
  ChoiceItem,
  IconContainer,
  LoadingIndicator,
  SectionHeader,
  Typography,
} from 'components/common'
import { LanguageBar } from 'components/features/Other/components/LanguageBar'
import { userApi } from 'components/features/User/api'
import {
  FMultiSelect,
  FSelect,
  FTextArea,
  FTextField,
  FToggle,
} from 'components/formik-factory'
import { Form, FormikProps, withFormik } from 'formik'
import { preventSubmitOnEnter } from 'helpers'
import { clamp } from 'helpers/num-helpers'
import { usePopulateMetadata } from 'hooks'
import { t } from 'i18next'
import { AlertCircle, Info } from 'react-feather'

import styles from './DocumentForm.module.scss'
import { documentFormSchema } from './DocumentForm.validation-schema'
import {
  DocumentsFormProps,
  DocumentsFormValues,
  FormMetadataValues,
  Option,
} from './types'
import {
  createEmptyMetadata,
  createInitialData,
  getFieldsByDocumentType,
  getMustChangeFields,
  mapMetadataToForm,
} from './utils'

const EmptyDiv = () => <div />

const DocumentsFormComponent = ({
  onValidityChange,
  practiceMetadata,
  defaultLanguage,
  titleIdPrefix,
  // onApplyMetadata,
  onValuesChange,
  onDirtyChange,
  onSubmit: _onSubmit, // used in 'withFormik' HOC
  defaultValues: _defaultValues, // used in 'withFormik' HOC
  errors,
  values,
  setFieldValue,
  isValid,
  dirty,
  validationLevel,
  hasMainDocument = false,
}: DocumentsFormProps & FormikProps<DocumentsFormValues>) => {
  const idPrefix = titleIdPrefix ? titleIdPrefix : ''
  const isStrictValidation =
    typeof validationLevel === 'string' && validationLevel === 'strict'

  const [languageTab, setLanguageTab] = useState(0)
  const [metadataApplied, setMetadataApplied] = useState(false)
  const [visibleFields, setVisibleFields] = useState<string[]>([])
  const [mustChangeFields, setMustChangeFields] = useState<string[]>([])

  const options = usePopulateMetadata('document')
  const userIdApi = userApi.useGetUserByIdQuery(
    values.metadata[languageTab]?.contact?.id ?? '',
    {
      skip:
        !values.metadata ||
        values.metadata.length - 1 > languageTab ||
        !values.metadata[languageTab] ||
        !values.metadata[languageTab].contact,
    }
  )

  useEffect(() => {
    setMetadataApplied(false)
  }, [practiceMetadata])

  useEffect(() => {
    if (onValidityChange) {
      onValidityChange(isValid, dirty)
    }
  }, [dirty, isValid, onValidityChange])

  useEffect(() => {
    if (onValuesChange) {
      onValuesChange(values)
    }
  }, [onValuesChange, values])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(dirty)
    }
  }, [dirty, onDirtyChange])

  useEffect(() => {
    if (values.type?.id) {
      setVisibleFields(
        getFieldsByDocumentType(values.type.id, validationLevel === 'strict')
      )
      setMustChangeFields(getMustChangeFields(values.type.id))
    } else {
      setVisibleFields([])
      setMustChangeFields([])
    }
  }, [values.type, validationLevel])

  useEffect(() => {
    if (
      !metadataApplied &&
      !!practiceMetadata &&
      practiceMetadata.length >= 1
    ) {
      setFieldValue(
        'metadata',
        mapMetadataToForm(practiceMetadata, values.metadata)
      )
      setMetadataApplied(true)
    }
  }, [
    practiceMetadata,
    metadataApplied,
    values.metadata,
    setMetadataApplied,
    setFieldValue,
  ])

  const getFieldName = (index: number) => (name: keyof FormMetadataValues) => {
    return `metadata.${index}.${name}`
  }

  const updateTab = (newTabValue: number) => {
    setLanguageTab(clamp(newTabValue))
  }

  const handleLanguage = {
    add: (language: string) => {
      setFieldValue('metadata', [
        ...values.metadata,
        createEmptyMetadata({ language }),
      ])

      setLanguageTab(values.metadata.length) // switches to latest tab
    },
    remove: (language: string) => {
      setFieldValue(
        'metadata',
        values.metadata.filter((option) => option.language !== language)
      )
      setLanguageTab(0) // switch to first tab
    },
  }

  return (
    <Form
      id={idPrefix + 'form'}
      className={styles.root}
      onKeyDown={preventSubmitOnEnter}
    >
      <div className={styles.row}>
        <FSelect
          label={t('documentForm.docType')}
          id="type"
          name="type"
          options={options.docTypes}
        />
        <FSelect
          label={t('documentForm.fileLanguage')}
          id="fileLanguage"
          name="fileLanguage"
          options={options.languages}
        />
      </div>
      {values.type?.id === 'main' &&
        (!_defaultValues || _defaultValues?.type?.id !== 'main') && (
          <div className={styles.row}>
            <Card noDropShadow>
              <Card.Body className={styles.notice}>
                <AlertCircle size={20} />
                {t(
                  hasMainDocument
                    ? 'documentForm.mainDocOverride'
                    : 'documentForm.mainDocOverridePotentially'
                )}
              </Card.Body>
            </Card>
          </div>
        )}
      {values.type && (
        <>
          <LanguageBar
            activeTabIndex={languageTab}
            languageOptions={options.languages}
            currentLanguages={values?.metadata?.map(({ language }) => language)}
            defaultLanguage={defaultLanguage}
            errors={null && errors && errors.metadata}
            onAddLanguage={handleLanguage.add}
            onRemoveLanguage={handleLanguage.remove}
            onTabClick={updateTab}
            className={styles.spacing}
          />

          {validationLevel !== 'strict' && (
            <div className={styles.spacing}>
              <ChoiceItem
                element={<FToggle name="divergentFieldsOnly" />}
                label={t('metadata.showDivergentFieldsOnly')}
                id={`toggleDivergentFields_${idPrefix}`}
                className={styles.choiceItem}
              />
            </div>
          )}

          {metadataApplied && (
            <Card color="primary" className={styles.card} noDropShadow>
              <Card.Body className={styles.body}>
                <IconContainer
                  className={styles.icon}
                  icon={<Info />}
                  color={'primary'}
                />
                <div className={styles.content}>
                  <Typography fontWeight="bold">
                    {values.type.id === 'main' && values.divergentFieldsOnly
                      ? t('documentForm.sameAsGuideline')
                      : t('documentForm.applyMetadataTitle')}
                  </Typography>
                  <Typography>
                    {values.type.id === 'main' && values.divergentFieldsOnly
                      ? t('documentForm.toggleDivergentFields')
                      : t('documentForm.applyMetadataSubtitle')}
                  </Typography>
                </div>
              </Card.Body>
            </Card>
          )}

          <>
            {values.metadata.map((item, index) => {
              const fieldName = getFieldName(index)

              return (
                <div
                  key={'new-doc-metadata' + item.language}
                  className={clsx(styles.formBody, {
                    [styles.active]: index === languageTab,
                    [styles.hidden]: !(
                      !values.divergentFieldsOnly ||
                      (values.divergentFieldsOnly &&
                        mustChangeFields.length > 0)
                    ),
                  })}
                >
                  <fieldset
                    className={styles.section}
                    id={idPrefix + 'general'}
                  >
                    <SectionHeader title={t('metadata.general')} small />
                    <div className={styles.row}>
                      <FTextField
                        name={fieldName('title')}
                        id={fieldName('title')}
                        label={t('metadata.title')}
                        helperText={t('documentForm.titleHelper')}
                      />
                      <EmptyDiv />
                    </div>
                    <div className={styles.row}>
                      <FTextField
                        optional
                        name={fieldName('subject')}
                        id={fieldName('subject')}
                        label={t('metadata.subject')}
                        helperText={t('documentForm.subjectHelper')}
                      />
                    </div>
                    <div className={styles.row}>
                      <FTextArea
                        optional={!isStrictValidation}
                        name={fieldName('description')}
                        id={fieldName('description')}
                        label={t('metadata.description')}
                        helperText={t('documentForm.descriptionHelper')}
                      />
                    </div>
                    <div
                      className={clsx(styles.row, {
                        [styles.hidden]: values.divergentFieldsOnly,
                      })}
                    >
                      <FTextField
                        optional
                        name={fieldName('code')}
                        id={fieldName('code')}
                        label={t('metadata.code')}
                      />
                      <EmptyDiv />
                    </div>
                  </fieldset>
                  <fieldset
                    className={clsx(styles.section, {
                      [styles.hidden]: values.divergentFieldsOnly,
                    })}
                    id={idPrefix + 'scope'}
                  >
                    <SectionHeader title={t('metadata.scope')} small />
                    <div
                      className={clsx(styles.row, {
                        [styles.hidden]:
                          !visibleFields.includes('departments') &&
                          !visibleFields.includes('targetGroup'),
                      })}
                    >
                      <FMultiSelect
                        optional={!isStrictValidation}
                        name={fieldName('departments')}
                        label={t('metadata.departments')}
                        data={options.departments as Option[]}
                        className={clsx({
                          [styles.hidden]:
                            !visibleFields.includes('departments'),
                        })}
                      />
                      <FTextField
                        optional={!isStrictValidation}
                        name={fieldName('target_group')}
                        id={fieldName('target_group')}
                        label={t('metadata.targetGroup')}
                      />
                    </div>
                    {
                      <div
                        className={clsx(styles.row, {
                          [styles.hidden]:
                            !visibleFields.includes('locations') &&
                            !visibleFields.includes('countries'),
                        })}
                      >
                        <FMultiSelect
                          optional={!isStrictValidation}
                          name={fieldName('locations')}
                          label={t('metadata.locations')}
                          data={options.locations as Option[]}
                          className={clsx({
                            [styles.hidden]:
                              !visibleFields.includes('locations'),
                          })}
                        />
                        <FMultiSelect
                          optional={!isStrictValidation}
                          name={fieldName('countries')}
                          label={t('metadata.countries')}
                          data={options.countries as Option[]}
                          className={clsx({
                            [styles.hidden]:
                              !visibleFields.includes('countries'),
                          })}
                        />
                      </div>
                    }
                    <div
                      className={clsx(styles.row, {
                        [styles.hidden]:
                          !visibleFields.includes('legalEntities') &&
                          !visibleFields.includes('effectiveAt'),
                      })}
                    >
                      <FMultiSelect
                        optional={!isStrictValidation}
                        name={fieldName('legal_entities')}
                        label={t('metadata.legalEntities')}
                        data={options.legalEntities as Option[]}
                        className={clsx({
                          [styles.hidden]:
                            !visibleFields.includes('legalEntities'),
                        })}
                      />
                      <FTextField
                        optional={!isStrictValidation}
                        name={fieldName('effective_at')}
                        id={fieldName('effective_at')}
                        label={t('metadata.effectiveAt')}
                        type="date"
                        className={clsx({
                          [styles.hidden]:
                            !visibleFields.includes('effectiveAt'),
                        })}
                      />
                      {!visibleFields.includes('legalEntities') && <EmptyDiv />}
                    </div>
                    <div
                      className={clsx(styles.row, {
                        [styles.hidden]: !visibleFields.includes('keywords'),
                      })}
                    >
                      <FMultiSelect
                        optional={!isStrictValidation}
                        name={fieldName('keywords')}
                        label={t('metadata.keywords')}
                        creatable
                        createOnBlur
                        hideDropdown
                      />
                    </div>
                  </fieldset>
                  <fieldset
                    className={clsx(styles.section, {
                      [styles.hidden]: !(
                        (visibleFields.includes('collections') ||
                          visibleFields.includes('certificationScope')) &&
                        !values.divergentFieldsOnly
                      ),
                    })}
                    id={idPrefix + 'references'}
                  >
                    <SectionHeader title={t('metadata.references')} small />
                    <div className={styles.row}>
                      <FMultiSelect
                        optional
                        name={fieldName('collections')}
                        label={t('metadata.collections')}
                        data={options.collections as Option[]}
                        className={clsx({
                          [styles.hidden]:
                            !visibleFields.includes('collections'),
                        })}
                      />
                      <FMultiSelect
                        optional
                        name={fieldName('certificationScope')}
                        label={t('metadata.scopeCertification')}
                        data={options.scopes as Option[]}
                        className={clsx({
                          [styles.hidden]:
                            !visibleFields.includes('certificationScope'),
                        })}
                      />
                    </div>
                  </fieldset>
                  <fieldset
                    className={clsx(styles.section, {
                      [styles.hidden]: !(
                        !values.divergentFieldsOnly &&
                        visibleFields.includes('contact')
                      ),
                    })}
                    id={idPrefix + 'contact'}
                  >
                    <SectionHeader title={t('metadata.contact')} small />

                    <div className={styles.row}>
                      <FSelect
                        label={t('metadata.teamMember')}
                        name={fieldName('contact')}
                        options={options.users}
                        dropdownPosition="top"
                      />
                      <EmptyDiv />
                    </div>

                    {userIdApi.isFetching && (
                      <div className={styles.row}>
                        <LoadingIndicator />
                        <EmptyDiv />
                      </div>
                    )}
                    {!userIdApi.isFetching && userIdApi.isSuccess && (
                      <div className={styles.userRow}>
                        <div className={styles.userRow}>
                          {userIdApi.data.data?.departments &&
                            userIdApi.data.data.departments.length > 0 && (
                              <div className={styles.userInfo}>
                                <Typography size="small" color="label">
                                  {t('documentForm.department')}
                                </Typography>
                                <Typography>
                                  {userIdApi.data.data.departments
                                    .map((dept) => dept.name)
                                    .join(', ')}
                                </Typography>
                              </div>
                            )}
                          <div className={styles.userInfo}>
                            <Typography size="small" color="label">
                              {t('documentForm.email')}
                            </Typography>
                            <Typography>{userIdApi.data.data.email}</Typography>
                          </div>
                        </div>
                        <EmptyDiv />
                      </div>
                    )}
                  </fieldset>
                </div>
              )
            })}
          </>
        </>
      )}
    </Form>
  )
}

const DocumentsForm = withFormik<DocumentsFormProps, DocumentsFormValues>({
  mapPropsToValues: (props) => {
    return {
      ...createInitialData(props.defaultValues),
      divergentFieldsOnly: props.validationLevel !== 'strict',
    }
  },

  mapPropsToTouched: (props) => {
    if (props.validateOnMount === true) {
      const initialData = createInitialData(props.defaultValues)

      return {
        divergentFieldsOnly: props.validationLevel !== 'strict',
        fileLanguage: true,
        type: true,
        metadata: initialData.metadata.map((dataObj) =>
          Object.keys(dataObj).reduce(
            (acc, key) => ({
              ...acc,
              [key]: true,
            }),
            {}
          )
        ),
      } as any
    }

    return {}
  },

  handleSubmit: (values, { props, setSubmitting }) => {
    if (props.onSubmit) {
      props.onSubmit(values)
      setSubmitting(false)
    }
  },

  validate: async (values: DocumentsFormValues, props) => {
    const errors: any = {}

    try {
      await documentFormSchema(
        values.divergentFieldsOnly,
        values.type?.id === 'main'
          ? props.validationRules.main
          : props.validationRules.default
      ).validate(values, { abortEarly: false })
    } catch (err: any) {
      err.inner.forEach((e: any) => {
        const path = e.path.replace('[', '.').replace(']', '')
        const parts = path.split('.')

        if (parts.length === 3) {
          const [metaKey, index, attribute] = parts

          if (!errors[metaKey]) {
            errors[metaKey] = []
          }

          if (!errors[metaKey][parseInt(index)]) {
            errors[metaKey][parseInt(index)] = {}
          }

          errors[metaKey][parseInt(index, 10)][attribute] = e.message
        } else {
          errors[e.path] = e.message
        }
      })
    }
    return errors
  },

  validateOnChange: true,
})(DocumentsFormComponent)

export default DocumentsForm
